/**
 * Bounding Box Utilities
 * 
 * Utilities for generating bounding boxes and collision shapes
 * from GLB/GLTF model meshes, including support for rigged models
 * with separate parts (arms, legs, branches, etc.)
 * 
 * Reference: https://doc.babylonjs.com/features/featuresDeepDive/mesh/boundingInfo
 */

import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { PhysicsShapeType } from "@babylonjs/core/Physics/v2/IPhysicsEnginePlugin";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";

/**
 * Computed bounding information for a model
 */
export interface ComputedBounds {
    /** Minimum corner of bounding box */
    min: Vector3;
    /** Maximum corner of bounding box */
    max: Vector3;
    /** Center point of bounding box */
    center: Vector3;
    /** Size of bounding box (width, height, depth) */
    size: Vector3;
    /** Half extents (size / 2) */
    halfExtents: Vector3;
    /** Bounding sphere radius */
    sphereRadius: number;
    /** Capsule radius (for capsule collision) */
    capsuleRadius: number;
    /** Capsule height (for capsule collision) */
    capsuleHeight: number;
}

/**
 * Bounds computed for each mesh part (for rigged/compound models)
 */
export interface MeshPartBounds {
    /** Mesh name */
    name: string;
    /** The mesh itself */
    mesh: AbstractMesh;
    /** Computed bounds for this part */
    bounds: ComputedBounds;
    /** Suggested collision shape */
    suggestedShape: PhysicsShapeType;
    /** Is this likely a main body part (trunk, torso)? */
    isMainPart: boolean;
}

/**
 * Calculate combined bounding box from multiple meshes
 * 
 * @param meshes - Array of meshes to compute bounds for
 * @param worldSpace - Whether to compute in world space (default: true)
 * @returns Computed bounding information
 */
export function computeBoundsFromMeshes(
    meshes: AbstractMesh[],
    worldSpace: boolean = true
): ComputedBounds {
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

    for (const mesh of meshes) {
        if (!mesh.isEnabled() || mesh.getTotalVertices() === 0) continue;

        // Force bounding info computation
        mesh.refreshBoundingInfo({ applySkeleton: true });
        const boundingInfo = mesh.getBoundingInfo();
        
        if (worldSpace) {
            // Use the already-computed world bounds directly
            const minWorld = boundingInfo.boundingBox.minimumWorld;
            const maxWorld = boundingInfo.boundingBox.maximumWorld;
            
            minX = Math.min(minX, minWorld.x);
            minY = Math.min(minY, minWorld.y);
            minZ = Math.min(minZ, minWorld.z);
            maxX = Math.max(maxX, maxWorld.x);
            maxY = Math.max(maxY, maxWorld.y);
            maxZ = Math.max(maxZ, maxWorld.z);
        } else {
            // Local space bounds
            const min = boundingInfo.boundingBox.minimum;
            const max = boundingInfo.boundingBox.maximum;
            
            minX = Math.min(minX, min.x);
            minY = Math.min(minY, min.y);
            minZ = Math.min(minZ, min.z);
            maxX = Math.max(maxX, max.x);
            maxY = Math.max(maxY, max.y);
            maxZ = Math.max(maxZ, max.z);
        }
    }

    // Handle empty mesh array
    if (minX === Infinity) {
        return {
            min: Vector3.Zero(),
            max: Vector3.Zero(),
            center: Vector3.Zero(),
            size: Vector3.Zero(),
            halfExtents: Vector3.Zero(),
            sphereRadius: 0,
            capsuleRadius: 0,
            capsuleHeight: 0,
        };
    }

    const min = new Vector3(minX, minY, minZ);
    const max = new Vector3(maxX, maxY, maxZ);
    const size = max.subtract(min);
    const center = min.add(size.scale(0.5));
    const halfExtents = size.scale(0.5);
    
    // Sphere radius - distance from center to corner
    const sphereRadius = halfExtents.length();
    
    // Capsule dimensions - use the horizontal extent as radius
    const horizontalRadius = Math.max(size.x, size.z) / 2;
    const capsuleRadius = horizontalRadius;
    const capsuleHeight = size.y;

    return {
        min,
        max,
        center,
        size,
        halfExtents,
        sphereRadius,
        capsuleRadius,
        capsuleHeight,
    };
}

/**
 * Create a collision mesh from computed bounds
 * The mesh can be used for physics collision
 * 
 * @param scene - Babylon.js scene
 * @param bounds - Computed bounds
 * @param shapeType - Desired collision shape
 * @param name - Name for the collision mesh
 * @returns Collision mesh
 */
export function createCollisionMeshFromBounds(
    scene: Scene,
    bounds: ComputedBounds,
    shapeType: PhysicsShapeType,
    name: string = "collisionMesh"
): Mesh {
    let collisionMesh: Mesh;

    switch (shapeType) {
        case PhysicsShapeType.BOX:
            collisionMesh = MeshBuilder.CreateBox(name, {
                width: bounds.size.x,
                height: bounds.size.y,
                depth: bounds.size.z,
            }, scene);
            break;

        case PhysicsShapeType.SPHERE:
            collisionMesh = MeshBuilder.CreateSphere(name, {
                diameter: bounds.sphereRadius * 2,
                segments: 16,
            }, scene);
            break;

        case PhysicsShapeType.CAPSULE:
            collisionMesh = MeshBuilder.CreateCapsule(name, {
                radius: bounds.capsuleRadius,
                height: bounds.capsuleHeight,
                tessellation: 16,
                capSubdivisions: 6,
            }, scene);
            break;

        case PhysicsShapeType.CYLINDER:
            collisionMesh = MeshBuilder.CreateCylinder(name, {
                diameter: Math.max(bounds.size.x, bounds.size.z),
                height: bounds.size.y,
                tessellation: 16,
            }, scene);
            break;

        default:
            // Default to box
            collisionMesh = MeshBuilder.CreateBox(name, {
                width: bounds.size.x,
                height: bounds.size.y,
                depth: bounds.size.z,
            }, scene);
    }

    // Position at the bounds center
    collisionMesh.position = bounds.center.clone();
    
    // Make collision mesh invisible
    collisionMesh.isVisible = false;

    return collisionMesh;
}

/**
 * Map CollisionMode to PhysicsShapeType
 */
export function collisionModeToPhysicsShape(mode: string): PhysicsShapeType {
    switch (mode) {
        case "bounding_box":
            return PhysicsShapeType.BOX;
        case "bounding_sphere":
            return PhysicsShapeType.SPHERE;
        case "capsule":
            return PhysicsShapeType.CAPSULE;
        case "cylinder":
            return PhysicsShapeType.CYLINDER;
        case "mesh":
            return PhysicsShapeType.MESH;
        case "convex_hull":
            return PhysicsShapeType.CONVEX_HULL;
        case "custom":
            // For custom mode, return BOX as default - caller should use customDimensions.shapeType
            return PhysicsShapeType.BOX;
        default:
            return PhysicsShapeType.BOX;
    }
}

/**
 * Print bounds information for debugging
 */
export function printBoundsInfo(bounds: ComputedBounds, modelName: string = "Model"): void {
    console.log(`=== Bounds Info: ${modelName} ===`);
    console.log(`  Min: (${bounds.min.x.toFixed(3)}, ${bounds.min.y.toFixed(3)}, ${bounds.min.z.toFixed(3)})`);
    console.log(`  Max: (${bounds.max.x.toFixed(3)}, ${bounds.max.y.toFixed(3)}, ${bounds.max.z.toFixed(3)})`);
    console.log(`  Center: (${bounds.center.x.toFixed(3)}, ${bounds.center.y.toFixed(3)}, ${bounds.center.z.toFixed(3)})`);
    console.log(`  Size: ${bounds.size.x.toFixed(3)} x ${bounds.size.y.toFixed(3)} x ${bounds.size.z.toFixed(3)}`);
    console.log(`  Sphere Radius: ${bounds.sphereRadius.toFixed(3)}`);
    console.log(`  Capsule: radius=${bounds.capsuleRadius.toFixed(3)}, height=${bounds.capsuleHeight.toFixed(3)}`);
    console.log(`================================`);
}

/**
 * Suggest the best collision shape for a model based on its dimensions
 */
export function suggestCollisionShape(bounds: ComputedBounds): {
    recommended: PhysicsShapeType;
    reason: string;
} {
    const { size } = bounds;
    const aspectRatioXZ = Math.max(size.x, size.z) / Math.min(size.x, size.z);
    const heightRatio = size.y / Math.max(size.x, size.z);

    // Tall and thin (like a tree trunk) - use cylinder
    if (heightRatio > 2 && aspectRatioXZ < 1.5) {
        return {
            recommended: PhysicsShapeType.CYLINDER,
            reason: "Tall cylindrical shape (good for trees, poles)"
        };
    }

    // Very tall (like a full tree with canopy) - use capsule or cylinder
    if (heightRatio > 1.5) {
        return {
            recommended: PhysicsShapeType.CAPSULE,
            reason: "Tall shape (good for trees, characters)"
        };
    }

    // Roughly spherical
    if (aspectRatioXZ < 1.3 && Math.abs(heightRatio - 1) < 0.3) {
        return {
            recommended: PhysicsShapeType.SPHERE,
            reason: "Roughly spherical shape"
        };
    }

    // Default to box for general shapes
    return {
        recommended: PhysicsShapeType.BOX,
        reason: "General boxy shape"
    };
}

/**
 * Compute bounds for each individual mesh part in a model
 * Useful for rigged models where each part (arm, leg, branch) might need separate collision
 * 
 * @param meshes - Array of meshes to analyze
 * @returns Array of bounds info for each mesh part
 */
export function computeBoundsPerPart(meshes: AbstractMesh[]): MeshPartBounds[] {
    const partBounds: MeshPartBounds[] = [];
    
    // Keywords to identify main body parts
    const mainPartKeywords = ['trunk', 'torso', 'body', 'main', 'base', 'stem', 'root'];
    
    for (const mesh of meshes) {
        if (!mesh.isEnabled() || mesh.getTotalVertices() === 0) continue;
        
        // Compute bounds for this specific mesh
        const bounds = computeBoundsFromMeshes([mesh], true);
        
        // Check if this is likely a main part based on name
        const lowerName = mesh.name.toLowerCase();
        const isMainPart = mainPartKeywords.some(keyword => lowerName.includes(keyword)) ||
                          bounds.size.y > bounds.size.x * 2; // Tall parts are often trunks
        
        // Suggest best collision shape for this part
        const suggestion = suggestCollisionShape(bounds);
        
        partBounds.push({
            name: mesh.name,
            mesh,
            bounds,
            suggestedShape: suggestion.recommended,
            isMainPart,
        });
    }
    
    return partBounds;
}

/**
 * Find the main collision part in a rigged model (e.g., tree trunk)
 * 
 * @param partBounds - Array of mesh part bounds
 * @returns The main part bounds or null if not found
 */
export function findMainCollisionPart(partBounds: MeshPartBounds[]): MeshPartBounds | null {
    // First look for explicitly tagged main parts
    const mainPart = partBounds.find(p => p.isMainPart);
    if (mainPart) return mainPart;
    
    // Otherwise, find the largest/tallest part
    if (partBounds.length === 0) return null;
    
    return partBounds.reduce((largest, current) => {
        const currentVolume = current.bounds.size.x * current.bounds.size.y * current.bounds.size.z;
        const largestVolume = largest.bounds.size.x * largest.bounds.size.y * largest.bounds.size.z;
        return currentVolume > largestVolume ? current : largest;
    });
}

/**
 * Create visible debug bounding boxes for all meshes
 * 
 * @param scene - Babylon.js scene
 * @param meshes - Array of meshes to visualize
 * @param color - Color for the debug boxes
 * @param parentNode - Optional parent node for the debug meshes
 * @returns Array of created debug line meshes
 */
export function createDebugBoundingBoxes(
    scene: Scene,
    meshes: AbstractMesh[],
    color: Color3 = new Color3(0, 1, 0),
    parentNode?: TransformNode
): Mesh[] {
    const debugMeshes: Mesh[] = [];
    
    for (const mesh of meshes) {
        if (!mesh.isEnabled() || mesh.getTotalVertices() === 0) continue;
        
        // Refresh bounding info
        mesh.refreshBoundingInfo({ applySkeleton: true });
        const boundingInfo = mesh.getBoundingInfo();
        
        // Create wireframe box
        const boxLines = createBoundingBoxWireframe(
            scene,
            boundingInfo.boundingBox.minimumWorld,
            boundingInfo.boundingBox.maximumWorld,
            color,
            `debug_bbox_${mesh.name}`
        );
        
        if (parentNode) {
            // For debug purposes, we keep world position
            // boxLines.parent = parentNode;
        }
        
        debugMeshes.push(boxLines);
    }
    
    return debugMeshes;
}

/**
 * Create a wireframe box between two corners
 */
export function createBoundingBoxWireframe(
    scene: Scene,
    min: Vector3,
    max: Vector3,
    color: Color3 = new Color3(0, 1, 0),
    name: string = "boundingBoxWireframe"
): Mesh {
    // Define the 8 corners of the bounding box
    const corners = [
        new Vector3(min.x, min.y, min.z), // 0 - bottom-front-left
        new Vector3(max.x, min.y, min.z), // 1 - bottom-front-right
        new Vector3(max.x, min.y, max.z), // 2 - bottom-back-right
        new Vector3(min.x, min.y, max.z), // 3 - bottom-back-left
        new Vector3(min.x, max.y, min.z), // 4 - top-front-left
        new Vector3(max.x, max.y, min.z), // 5 - top-front-right
        new Vector3(max.x, max.y, max.z), // 6 - top-back-right
        new Vector3(min.x, max.y, max.z), // 7 - top-back-left
    ];
    
    // Define the 12 edges as line segments
    const lines = [
        // Bottom face
        [corners[0], corners[1]],
        [corners[1], corners[2]],
        [corners[2], corners[3]],
        [corners[3], corners[0]],
        // Top face
        [corners[4], corners[5]],
        [corners[5], corners[6]],
        [corners[6], corners[7]],
        [corners[7], corners[4]],
        // Vertical edges
        [corners[0], corners[4]],
        [corners[1], corners[5]],
        [corners[2], corners[6]],
        [corners[3], corners[7]],
    ];
    
    const lineSystem = MeshBuilder.CreateLineSystem(name, { lines }, scene);
    lineSystem.color = color;
    
    return lineSystem;
}

/**
 * Create a debug collision mesh (visible wireframe showing collision shape)
 * 
 * @param scene - Babylon.js scene
 * @param bounds - Computed bounds
 * @param shapeType - Physics shape type
 * @param color - Color for debug visualization
 * @param name - Name for the mesh
 * @returns Visible collision mesh for debugging
 */
export function createDebugCollisionMesh(
    scene: Scene,
    bounds: ComputedBounds,
    shapeType: PhysicsShapeType,
    color: Color3 = new Color3(0, 1, 0),
    name: string = "debugCollisionMesh"
): Mesh {
    let debugMesh: Mesh;
    
    switch (shapeType) {
        case PhysicsShapeType.BOX:
            debugMesh = MeshBuilder.CreateBox(name, {
                width: bounds.size.x,
                height: bounds.size.y,
                depth: bounds.size.z,
            }, scene);
            break;
            
        case PhysicsShapeType.SPHERE:
            debugMesh = MeshBuilder.CreateSphere(name, {
                diameter: bounds.sphereRadius * 2,
                segments: 16,
            }, scene);
            break;
            
        case PhysicsShapeType.CAPSULE:
            debugMesh = MeshBuilder.CreateCapsule(name, {
                radius: bounds.capsuleRadius,
                height: bounds.capsuleHeight,
                tessellation: 16,
                capSubdivisions: 6,
            }, scene);
            break;
            
        case PhysicsShapeType.CYLINDER:
            debugMesh = MeshBuilder.CreateCylinder(name, {
                diameter: Math.max(bounds.size.x, bounds.size.z),
                height: bounds.size.y,
                tessellation: 16,
            }, scene);
            break;
            
        default:
            debugMesh = MeshBuilder.CreateBox(name, {
                width: bounds.size.x,
                height: bounds.size.y,
                depth: bounds.size.z,
            }, scene);
    }
    
    // Position at bounds center
    debugMesh.position = bounds.center.clone();
    
    // Create wireframe material
    const material = new StandardMaterial(`${name}_material`, scene);
    material.wireframe = true;
    material.emissiveColor = color;
    material.disableLighting = true;
    debugMesh.material = material;
    
    return debugMesh;
}

/**
 * Print detailed mesh hierarchy information for debugging
 * 
 * @param meshes - Array of meshes to analyze
 * @param modelName - Name for logging
 */
export function printMeshHierarchy(meshes: AbstractMesh[], modelName: string = "Model"): void {
    console.log(`\n=== Mesh Hierarchy: ${modelName} ===`);
    console.log(`Total meshes: ${meshes.length}`);
    
    for (const mesh of meshes) {
        const hasVertices = mesh.getTotalVertices() > 0;
        const bounds = hasVertices ? mesh.getBoundingInfo() : null;
        
        console.log(`\n  [${mesh.name}]`);
        console.log(`    Enabled: ${mesh.isEnabled()}`);
        console.log(`    Vertices: ${mesh.getTotalVertices()}`);
        console.log(`    Parent: ${mesh.parent?.name || 'none'}`);
        
        if (bounds) {
            const size = bounds.boundingBox.maximum.subtract(bounds.boundingBox.minimum);
            console.log(`    Local Bounds: ${size.x.toFixed(3)} x ${size.y.toFixed(3)} x ${size.z.toFixed(3)}`);
        }
        
        // Check for skeleton/bones
        if (mesh.skeleton) {
            console.log(`    Skeleton: ${mesh.skeleton.name} (${mesh.skeleton.bones.length} bones)`);
        }
    }
    console.log(`\n${'='.repeat(40)}\n`);
}

/**
 * Create wireframe visualization of actual mesh geometry
 * This provides a much clearer view of what the collision shape looks like
 * 
 * @param scene - Babylon.js scene
 * @param meshes - Array of meshes to create wireframes for
 * @param color - Color for the wireframe
 * @param parentNode - Parent node to attach debug meshes to
 * @param name - Base name for debug meshes
 * @returns Array of wireframe debug meshes
 */
export function createMeshWireframeDebug(
    scene: Scene,
    meshes: AbstractMesh[],
    color: Color3 = new Color3(0, 1, 0),
    parentNode?: TransformNode,
    name: string = "debug_wireframe"
): Mesh[] {
    const debugMeshes: Mesh[] = [];
    
    for (const mesh of meshes) {
        if (!mesh.isEnabled() || mesh.getTotalVertices() === 0) continue;
        if (!(mesh instanceof Mesh)) continue;
        
        // Clone the mesh for wireframe visualization
        const wireframeMesh = mesh.clone(`${name}_${mesh.name}`, parentNode ?? null);
        if (!wireframeMesh) continue;
        
        // Create wireframe material
        const material = new StandardMaterial(`${name}_${mesh.name}_mat`, scene);
        material.wireframe = true;
        material.emissiveColor = color;
        material.disableLighting = true;
        material.backFaceCulling = false;
        
        wireframeMesh.material = material;
        wireframeMesh.isPickable = false;
        
        // Ensure it renders on top slightly for visibility
        wireframeMesh.renderingGroupId = 1;
        
        debugMeshes.push(wireframeMesh);
    }
    
    return debugMeshes;
}

/**
 * Create edge outline visualization of mesh geometry
 * Uses edge rendering for cleaner lines
 * 
 * @param scene - Babylon.js scene  
 * @param meshes - Array of meshes to outline
 * @param color - Color for the outline
 * @param parentNode - Parent node to attach debug meshes to
 * @param name - Base name for debug meshes
 * @returns Array of outline debug meshes
 */
export function createMeshOutlineDebug(
    scene: Scene,
    meshes: AbstractMesh[],
    color: Color3 = new Color3(0, 1, 0),
    parentNode?: TransformNode,
    name: string = "debug_outline"
): Mesh[] {
    const debugMeshes: Mesh[] = [];
    
    for (const mesh of meshes) {
        if (!mesh.isEnabled() || mesh.getTotalVertices() === 0) continue;
        if (!(mesh instanceof Mesh)) continue;
        
        // Clone the mesh for outline visualization
        const outlineMesh = mesh.clone(`${name}_${mesh.name}`, parentNode ?? null);
        if (!outlineMesh) continue;
        
        // Enable edge rendering on the clone
        outlineMesh.enableEdgesRendering();
        outlineMesh.edgesWidth = 2.0;
        outlineMesh.edgesColor = new Color4(color.r, color.g, color.b, 1);
        
        // Make the mesh itself invisible, only show edges
        const material = new StandardMaterial(`${name}_${mesh.name}_mat`, scene);
        material.alpha = 0;
        outlineMesh.material = material;
        outlineMesh.isPickable = false;
        
        debugMeshes.push(outlineMesh);
    }
    
    return debugMeshes;
}
